/**
 * #605 — the handoff's display filter as ONE implementation, with no schema
 * library behind it: Papan's picker and server (`@papan/types/file-handoff`
 * re-exports it) and the SDK's drop from the device (bundled into the browser
 * SDK) apply the same rule, byte for byte.
 */
import { isUninformativeMime, mimeFromExt } from './mime.js'

/** What the matcher reads of a handoff filter (`HandoffFilter`, validated
 *  elsewhere). */
export interface HandoffFilterFields {
  mime?: readonly string[]
  extensions?: readonly string[]
  nameGlob?: string
}

/**
 * What an app may not put in a name it saves under — anything that makes a
 * name read as another. In a save of several the person cannot rename, and a
 * `.html` saved as "report.pdf" later runs as them.
 *   - format characters (`\p{Cf}`): bidi embeddings, overrides and isolates
 *     (`report‮fdp.html` shows as `reportlmth.pdf`), direction marks, the
 *     zero-width space, the BOM, the soft hyphen, word joiners, tag
 *     characters… — EXCEPT the zero-width non-joiner and joiner (U+200C–200D):
 *     they reorder nothing, and real names need them (Persian spelling, emoji
 *     sequences, Indic conjuncts);
 *   - line and paragraph separators, and C1 controls;
 *   - a blank that is not a SPACE (tabs, newlines, vertical tabs), the
 *     invisible not-quite-blanks (Hangul fillers, the braille blank), and a
 *     run of four spaces or more: blank padding pushes the real extension out
 *     of sight (`Q3-report.pdf<240 blanks>.html`), which four cannot do.
 *     Every space separator — ordinary, no-break, narrow no-break,
 *     ideographic — is a real name character, singly and in twos and threes:
 *     macOS names every screenshot `Screenshot … at 10.00.00\u202FAM.png`,
 *     CJK names use U+3000, and `Q3 Report   Final.pdf` is somebody's file.
 *     A rule stricter than the drive's own `fileName` would refuse a name
 *     Papan itself created, which an app saving back what a pick delivered
 *     hits first.
 * The server's schema and the SDK refuse the same set, of the trimmed name.
 */
export const HANDOFF_NAME_SPOOF =
  /(?![\u200C\u200D])\p{Cf}|[\p{Zl}\p{Zp}\u0080-\u009F\u115F\u1160\u2800\u3164\uFFA0]|(?!\p{Zs})\s|\p{Zs}{4}/u

/** The longest name glob an app may give. */
export const MAX_GLOB_LENGTH = 64

/** An extension as the filter keeps it: trimmed, leading dots dropped,
 *  lowercased (`' .XLSX'` → `'xlsx'`). The schema applies it on the way in. */
export function normalizeFilterExtension(ext: string): string {
  return ext.trim().replace(/^\.+/, '').toLowerCase()
}

/** A media type as the filter keeps it: trimmed, lowercased. */
export function normalizeFilterMime(mime: string): string {
  return mime.trim().toLowerCase()
}

/** A filter as the app wrote it, in the form Papan's picker and server match
 *  against — for a caller that has not been through the schema (the SDK's
 *  drop from the device). */
export function normalizeFilterFields(filter: HandoffFilterFields | null | undefined): HandoffFilterFields | null {
  if (!filter) return null
  return {
    ...(filter.mime ? { mime: filter.mime.map(normalizeFilterMime) } : {}),
    ...(filter.extensions ? { extensions: filter.extensions.map(normalizeFilterExtension) } : {}),
    ...(filter.nameGlob !== undefined ? { nameGlob: filter.nameGlob } : {}),
  }
}

// ─── The display filter ──────────────────────────────────────────────────────

/**
 * Does a file pass the filter? Like HTML's `accept`, the TYPE criteria are
 * alternatives — a file passes when its extension OR its media type is listed
 * (when either list is given). `nameGlob`, when given, must ALSO match.
 *
 * `mime` is the stored media type; an uninformative one (null,
 * `octet-stream`, archives) falls back to the extension's, because many
 * stored rows predate the full MIME table.
 */
export function matchesHandoffFilter(
  file: { name: string; mime?: string | null },
  filter: HandoffFilterFields | null | undefined,
): boolean {
  if (!filter) return true
  const hasTypeCriteria = !!(filter.extensions?.length || filter.mime?.length)
  if (hasTypeCriteria && !matchesExtension(file.name, filter.extensions) && !matchesMime(effectiveMime(file), filter.mime)) {
    return false
  }
  if (filter.nameGlob !== undefined && !matchNameGlob(filter.nameGlob, file.name)) return false
  return true
}

/** The media type a handoff reports for a file: the stored one when it says
 *  something, else the extension's. Never empty. */
export function effectiveMime(file: { name: string; mime?: string | null }): string {
  return isUninformativeMime(file.mime) ? mimeFromExt(file.name) : file.mime!.toLowerCase()
}

function matchesExtension(name: string, extensions: readonly string[] | undefined): boolean {
  if (!extensions?.length) return false
  const lower = name.toLowerCase()
  // `length > ext + 1` so a dotfile (`.csv`) has no extension, matching
  // `extensionOf`: there must be a basename before the dot.
  return extensions.some((ext) => lower.length > ext.length + 1 && lower.endsWith(`.${ext}`))
}

function matchesMime(mime: string, patterns: readonly string[] | undefined): boolean {
  if (!patterns?.length) return false
  return patterns.some((p) => (p.endsWith('/*') ? mime.startsWith(p.slice(0, -1)) : mime === p))
}

// ─── Name glob — a bit-parallel matcher, deliberately NOT a RegExp ──────────
//
// The glob comes from an untrusted app and is evaluated on the SERVER for
// every descendant of an imported folder, up to the walk's row budget. A
// RegExp turns `*a*a*a*b` into catastrophic backtracking (O(n^stars)); even the
// classic two-pointer wildcard match re-scans on every mismatch, O(name × glob)
// token tests — a long near-miss glob over a folder of long names cost seconds
// of CPU per measure (#605 round 10).
//
// This is the glob's NFA run as a bitset (shift-and): ONE pass over the name,
// a few word operations per character. State j = "the first j non-star tokens
// matched"; a `*` is a self-loop on the state it follows. Which tokens a
// character satisfies is its MASK, computed once per character per glob and
// cached — so the token tests (the class ranges) run per distinct character,
// not per step. The worst case (a name of never-seen characters) is bounded by
// the glob's length: at most MAX_GLOB_LENGTH tests per character.

type GlobToken =
  | { kind: 'one' }
  | { kind: 'char'; ch: string }
  | { kind: 'class'; negate: boolean; items: Array<[string, string]> }

interface CompiledGlob {
  /** Non-star tokens, in order; state `tokens.length` accepts. */
  tokens: GlobToken[]
  /** 32-bit words per state set. */
  words: number
  /** States a `*` follows — they stay active on any character. */
  loops: Int32Array
  /** Character → the token bits it satisfies (token j sets bit j + 1). */
  masks: Map<string, Int32Array>
}

// Both caches bounded so their worst case stays small (~64 × 256 masks).
const globCache = new Map<string, CompiledGlob | null>()
const GLOB_CACHE_MAX = 64
/** Distinct characters remembered per glob; past it the cache starts over. */
const MASK_CACHE_MAX = 256

/** `null` when the glob is malformed (unbalanced `[`, empty class). */
function compileNameGlob(glob: string): CompiledGlob | null {
  const cached = globCache.get(glob)
  if (cached !== undefined) return cached
  const compiled = compileGlob(glob)
  if (globCache.size >= GLOB_CACHE_MAX) globCache.delete(globCache.keys().next().value!)
  globCache.set(glob, compiled)
  return compiled
}

function compileGlob(glob: string): CompiledGlob | null {
  const chars = Array.from(glob.toLowerCase())
  const tokens: GlobToken[] = []
  /** The states a `*` follows (`**` ≡ `*` for a name). */
  const loopStates: number[] = []
  for (let i = 0; i < chars.length; i++) {
    const c = chars[i]
    if (c === '*') {
      if (loopStates[loopStates.length - 1] !== tokens.length) loopStates.push(tokens.length)
    } else if (c === '?') {
      tokens.push({ kind: 'one' })
    } else if (c === '[') {
      let j = i + 1
      let negate = false
      if (chars[j] === '!' || chars[j] === '^') { negate = true; j++ }
      const items: Array<[string, string]> = []
      // A `]` right after the opening (or after the negation) is a literal member.
      let first = true
      while (j < chars.length && (chars[j] !== ']' || first)) {
        first = false
        const lo = chars[j]
        if (chars[j + 1] === '-' && j + 2 < chars.length && chars[j + 2] !== ']') {
          const hi = chars[j + 2]
          if (hi < lo) return null
          items.push([lo, hi])
          j += 3
        } else {
          items.push([lo, lo])
          j++
        }
      }
      if (j >= chars.length || items.length === 0) return null // unbalanced / empty
      tokens.push({ kind: 'class', negate, items })
      i = j
    } else {
      tokens.push({ kind: 'char', ch: c })
    }
  }
  const words = (tokens.length >>> 5) + 1
  const loops = new Int32Array(words)
  for (const s of loopStates) loops[s >>> 5] |= 1 << (s & 31)
  return { tokens, words, loops, masks: new Map() }
}

function tokenMatches(t: GlobToken, ch: string): boolean {
  switch (t.kind) {
    case 'one': return true
    case 'char': return t.ch === ch
    case 'class': {
      const hit = t.items.some(([lo, hi]) => ch >= lo && ch <= hi)
      return t.negate ? !hit : hit
    }
  }
}

function maskFor(g: CompiledGlob, ch: string): Int32Array {
  const cached = g.masks.get(ch)
  if (cached) return cached
  const mask = new Int32Array(g.words)
  for (let j = 0; j < g.tokens.length; j++) {
    const bit = j + 1
    if (tokenMatches(g.tokens[j], ch)) mask[bit >>> 5] |= 1 << (bit & 31)
  }
  if (g.masks.size >= MASK_CACHE_MAX) g.masks.clear()
  g.masks.set(ch, mask)
  return mask
}

/** `true` when the glob is well-formed. */
export function isValidNameGlob(glob: string): boolean {
  return glob.length > 0 && glob.length <= MAX_GLOB_LENGTH && compileNameGlob(glob) !== null
}

/** Case-insensitive glob match over a file name. A malformed glob matches nothing. */
export function matchNameGlob(glob: string, name: string): boolean {
  const g = compileNameGlob(glob)
  if (!g) return false
  const { words, loops } = g
  let state = new Int32Array(words)
  let next = new Int32Array(words)
  state[0] = 1 // nothing matched yet
  for (const ch of name.toLowerCase()) {
    const mask = maskFor(g, ch)
    let carry = 0
    let alive = 0
    for (let w = 0; w < words; w++) {
      const s = state[w]
      // Advance every active state by one token (where this character
      // satisfies it), and keep every state a `*` follows.
      const v = (((s << 1) | carry) & mask[w]) | (s & loops[w])
      carry = s >>> 31
      next[w] = v
      alive |= v
    }
    if (!alive) return false
    const t = state
    state = next
    next = t
  }
  const accept = g.tokens.length
  return (state[accept >>> 5] & (1 << (accept & 31))) !== 0
}
